import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Input, Select, Space, Spin, Tag, Typography } from "antd";
import { useState } from "react";

import { Button as ZhButton, Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import type { ActivityRelatedProductItem } from "./types";

const productTypeLabels: Record<number, string> = { 1: "寄售", 2: "团购", 3: "捡漏", 4: "活动" };

export interface ActivityRelatedProductCandidate {
    productId?: number | string;
    type?: number;
    product?: {
        title?: string;
        productTypeName?: string;
        supplierName?: string;
        supplierEnterprise?: { name?: string; enterpriseName?: string; supplierName?: string };
    };
}

export interface ActivityRelatedProductSelectorResult {
    list?: ActivityRelatedProductCandidate[];
    total?: number;
}

export interface ActivityRelatedProductSelectorProps {
    value?: ActivityRelatedProductItem[] | string;
    onChange?: (value: ActivityRelatedProductItem[] | string) => void;
    loadCandidates?: (query: {
        page: number;
        size: number;
        keyword?: string;
        productTypeName?: string;
        supplierName?: string;
        type?: number;
        excludeProducts?: Array<{ productId: string; type: number }>;
    }) => Promise<ActivityRelatedProductSelectorResult>;
}

/** 复用 Vue 商品选择器接口，保留活动表单的商品类型和排除项契约。 */
export async function loadActivityRelatedProductCandidates(query: {
    page: number;
    size: number;
    keyword?: string;
    productTypeName?: string;
    supplierName?: string;
    type?: number;
    excludeProducts?: Array<{ productId: string; type: number }>;
}): Promise<ActivityRelatedProductSelectorResult> {
    const response = await request.service.post<ActivityRelatedProductSelectorResult>(
        "/admin/product/campaign-paging-selector",
        query
    );
    return response.data;
}

const normalizeItems = (value?: ActivityRelatedProductItem[] | string) => {
    if (Array.isArray(value)) {
        return value
            .map(item => {
                const id = item.id ?? item.productId ?? item.productCampaign?.code ?? item.productCampaign?.id;
                return item.type == null || id == null ? "" : `${item.type}:${id}`;
            })
            .filter(Boolean);
    }
    return String(value || "")
        .split(",")
        .map(item => item.trim())
        .filter(item => item.includes(":"));
};

/** 将候选商品转换成 Vue 关联商品提交所需的 type:id 文本。 */
const candidateKey = (candidate: ActivityRelatedProductCandidate) =>
    candidate.productId == null || candidate.type == null ? "" : `${candidate.type}:${candidate.productId}`;

/** 兼容 Vue 商品候选接口的嵌套企业名称和扁平 supplierName 返回结构。 */
const getCandidateSupplierName = (candidate: ActivityRelatedProductCandidate) =>
    candidate.product?.supplierEnterprise?.name ||
    candidate.product?.supplierEnterprise?.enterpriseName ||
    candidate.product?.supplierEnterprise?.supplierName ||
    candidate.product?.supplierName ||
    "-";

type ActivityRelatedProductFilterState = {
    productTypeName: string;
    supplierName: string;
    candidateType: string;
};

/** 活动表单的关联商品选择器，type=4 活动商品仍通过原有文本结构兼容编辑。 */
export function ActivityRelatedProductSelector({
    value,
    onChange,
    loadCandidates = loadActivityRelatedProductCandidates
}: ActivityRelatedProductSelectorProps) {
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectorError, setSelectorError] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<ActivityRelatedProductCandidate[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [productTypeName, setProductTypeName] = useState("");
    const [supplierName, setSupplierName] = useState("");
    const [candidateType, setCandidateType] = useState("");
    const selectedItems = normalizeItems(value);

    const queryCandidates = async (
        nextKeyword = keyword,
        nextPage = 1,
        filterOverrides?: Partial<ActivityRelatedProductFilterState>
    ) => {
        const filters: ActivityRelatedProductFilterState = {
            productTypeName,
            supplierName,
            candidateType,
            ...filterOverrides
        };
        setLoading(true);
        setSelectorError(null);
        setPage(nextPage);
        try {
            // 仅将 Vue 寄售、团购、捡漏三类合法文本转换为排除参数，避免 NaN 污染请求。
            const exclusionMap = new Map<string, { productId: string; type: number }>();
            selectedItems
                .map(item => item.split(":"))
                .filter(([type, id]) => ["1", "2", "3"].includes(type) && Boolean(id))
                .forEach(([type, id]) => {
                    exclusionMap.set(`${type}:${id}`, { productId: id, type: Number(type) });
                });
            const excludeProducts = Array.from(exclusionMap.values());
            const result = await loadCandidates({
                page: nextPage,
                size: 10,
                ...(nextKeyword.trim() ? { keyword: nextKeyword.trim() } : {}),
                ...(filters.productTypeName.trim() ? { productTypeName: filters.productTypeName.trim() } : {}),
                ...(filters.supplierName.trim() ? { supplierName: filters.supplierName.trim() } : {}),
                ...(filters.candidateType ? { type: Number(filters.candidateType) } : {}),
                ...(excludeProducts.length ? { excludeProducts } : {})
            });
            setCandidates(result.list || []);
            setTotal(result.total || 0);
        } catch {
            // Vue 选择器请求失败时保留弹窗和已选项，允许用户修正搜索条件后重试。
            setSelectorError("活动商品候选加载失败");
        } finally {
            setLoading(false);
        }
    };

    const removeItem = (index: number) => {
        if (Array.isArray(value)) {
            onChange?.(value.filter((_item, itemIndex) => itemIndex !== index));
            return;
        }
        onChange?.(selectedItems.filter((_item, itemIndex) => itemIndex !== index).join(","));
    };

    const addCandidate = (candidate: ActivityRelatedProductCandidate) => {
        const key = candidateKey(candidate);
        if (!key || selectedItems.includes(key)) return;
        if (Array.isArray(value)) {
            onChange?.([...value, { productId: candidate.productId, type: candidate.type }]);
            return;
        }
        onChange?.([...selectedItems, key].join(","));
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 关联商品选择表格及添加操作插槽。
    const candidateColumns: TableColumnProps<ActivityRelatedProductCandidate>[] = [
        {
            key: "productId",
            label: "商品编号",
            width: 176,
            prop: "productId",
            render: ({ row }) => row.productId || "-"
        },
        {
            key: "title",
            label: "商品名称",
            width: 350,
            prop: "title",
            render: ({ row }) => row.product?.title || "-"
        },
        {
            key: "productTypeName",
            label: "品类",
            prop: "productTypeName",
            render: ({ row }) => row.product?.productTypeName || "-"
        },
        {
            key: "supplierName",
            label: "供应商",
            minWidth: 148,
            prop: "supplierName",
            render: ({ row }) => getCandidateSupplierName(row)
        },
        {
            key: "type",
            label: "商品类型",
            prop: "type",
            render: ({ row }) => productTypeLabels[row.type || 0] || "-"
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            render: ({ row }) => {
                const title = row.product?.title || String(row.productId || "商品");
                const key = candidateKey(row);
                const added = selectedItems.includes(key);
                return (
                    <ZhButton aria-label={`添加${title}`} disabled={!key || added} onClick={() => addCandidate(row)}>
                        {added ? "已添加" : "添加"}
                    </ZhButton>
                );
            }
        }
    ];

    return (
        <Space orientation="vertical" className="w-full">
            <Space.Compact className="w-full">
                <Input
                    aria-label="关联商品配置"
                    placeholder="请选择关联商品"
                    readOnly={false}
                    value={selectedItems.join(",")}
                    onChange={event => onChange?.(event.target.value)}
                />
                <ZhButton
                    onClick={() => {
                        setOpen(true);
                        void queryCandidates("", 1);
                    }}
                >
                    选择关联商品
                </ZhButton>
            </Space.Compact>
            <Space wrap>
                {selectedItems.map((item, index) => (
                    <Tag key={`${item}-${index}`} closable onClose={() => removeItem(index)}>
                        {item}
                    </Tag>
                ))}
            </Space>
            <ZhDialog
                footer={<ZhButton onClick={() => setOpen(false)}>确定选择</ZhButton>}
                open={open}
                title="选择关联商品"
                width={1200}
                zIndex={1000}
                onCancel={() => setOpen(false)}
            >
                <Space orientation="vertical" className="w-full">
                    <Spin spinning={loading}>
                        <ZhTable<ActivityRelatedProductCandidate>
                            data={candidates}
                            columns={normalizeOperationColumns(candidateColumns, candidates)}
                            pagination={false}
                            rowKey={candidate => candidateKey(candidate)}
                            isShowSearchBtn={false}
                            isShowResetBtn={false}
                            tableHeader={
                                <div className="admin-table-composed-header gap-3">
                                    <Space.Compact className="w-full">
                                        <Input
                                            aria-label="商品搜索"
                                            placeholder="请输入商品名称/商品编号"
                                            value={keyword}
                                            onChange={event => setKeyword(event.target.value)}
                                        />
                                        <ZhButton onClick={() => void queryCandidates(keyword, 1)}>搜索商品</ZhButton>
                                        <ZhButton
                                            onClick={() => {
                                                // Vue 表格重置后立即按默认条件重新请求第一页。
                                                setKeyword("");
                                                setProductTypeName("");
                                                setSupplierName("");
                                                setCandidateType("");
                                                void queryCandidates("", 1, {
                                                    productTypeName: "",
                                                    supplierName: "",
                                                    candidateType: ""
                                                });
                                            }}
                                        >
                                            重置商品筛选
                                        </ZhButton>
                                    </Space.Compact>
                                    <Space wrap>
                                        <Input
                                            aria-label="候选商品品类"
                                            placeholder="请输入品类"
                                            value={productTypeName}
                                            onChange={event => setProductTypeName(event.target.value)}
                                        />
                                        <Input
                                            aria-label="候选商品供应商"
                                            placeholder="请输入供应商名称"
                                            value={supplierName}
                                            onChange={event => setSupplierName(event.target.value)}
                                        />
                                        <Select
                                            allowClear
                                            aria-label="候选商品类型"
                                            options={[1, 2, 3].map(type => ({
                                                label: productTypeLabels[type],
                                                value: String(type)
                                            }))}
                                            placeholder="请选择商品类型"
                                            value={candidateType || undefined}
                                            onChange={value => setCandidateType(value || "")}
                                        />
                                    </Space>
                                    {selectorError ? (
                                        <Typography.Text type="danger" role="alert">
                                            {selectorError}
                                        </Typography.Text>
                                    ) : null}
                                </div>
                            }
                            append={
                                <div className="admin-table-composed-pagination flex-col items-end gap-2">
                                    <ZhPagination
                                        current={page}
                                        pageSize={10}
                                        hideOnSinglePage
                                        total={total}
                                        // 沿用 Vue 选择器分页契约，翻页时保留当前搜索和筛选条件。
                                        onChange={nextPage => void queryCandidates(keyword, nextPage)}
                                    />
                                    <Typography.Text type="secondary">
                                        已选择 {selectedItems.length} 个商品
                                    </Typography.Text>
                                </div>
                            }
                        />
                    </Spin>
                </Space>
            </ZhDialog>
        </Space>
    );
}

export default ActivityRelatedProductSelector;
